import {
  provideHttpClient,
  withInterceptors,
  withJsonpSupport,
} from '@angular/common/http';
import {
  ApplicationConfig,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeKo from '@angular/common/locales/ko';
import localeJa from '@angular/common/locales/ja';
import { provideClientHydration } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { RxStomp } from '@stomp/rx-stomp';
import { DIALOG_SERVICE } from 'src/lib/guards/login.guard';
import { BrandSearchKeyStateItem } from 'src/lib/states/brand-search-key.state-item';
import { HomepageBuilderSearchKeyStateItem } from 'src/lib/states/homepage-builder-search-key.state-item';
import { cacheInterceptor } from 'src/lib/interceptor/cache.interceptor';
import { register as registerSwiper } from 'swiper/element';
import { provideEchartsCore } from 'ngx-echarts';
import { environment } from '../environments/environment';
import { authInterceptor } from '../lib/auth/auth.interceptor';
// import {
//   PW_EDITOR_OPTION,
//   PwEditorOption,
// } from '../lib/components/pw-summernote/pw-summernote.component';
import {
  PW_EDITOR_OPTION,
  PwEditorOption,
} from '../lib/components/pw-summernote/pw-summernote.option';
import { errorToastInterceptor } from '../lib/interceptor/error-toast.interceptor';
import { loggingInterceptor } from '../lib/interceptor/logging.interceptor';
import { actingBrandInterceptor } from '../lib/interceptor/acting-brand.interceptor';
import { provideGlobalRequestHandler } from '../lib/repository/global-request-handler';
import { RestaurantGoodsPageStateItem } from '../lib/repository/restaurant-goods/restaurant-goods-page.state-item';
import {
  PW_UPDATE_OPTION,
  PwUpdateOption,
} from '../lib/services/app-update.service';
import { PlatformService } from '../lib/services/platform.service';
import { RxStompService } from '../lib/services/rx-stomp.service';
import { StorageService } from '../lib/services/storage.service';
import { AdminSearchKeyStateItem } from '../lib/states/admin-search-keyword.state-item';
import { AttractionMrhstSearchKeyStateItem } from '../lib/states/attraction-mrhst-search-keyword.state-item';
import { BrandPageStateItem } from '../lib/states/brand-page.state-item';
import { CarGoodsSearchKeyStateItem } from '../lib/states/car-goods-search-key.state-item';
import { CarMrhstSearchKeyStateItem } from '../lib/states/car-mrhst-search-keyword.state-item';
import { CodemasterServiceRegionCodeStateItem } from '../lib/states/codemaster-service-region-code.state-item';
import { CodemasterUserClassSearchKeyStateItem } from '../lib/states/codemaster-user-class.state-item';
import { HotelMrhstSearchKeyStateItem } from '../lib/states/hotel-mrhst-search-keyword.state-item';
import { PackagegoodsAvailMrhstStateItem } from '../lib/states/packagegoods-avail-mrhst.state-item';
import { PackagegoodsBookingListStateItem } from '../lib/states/packagegoods-booking-list.state-item';
import { PackagegoodsSearchKeyStateItem } from '../lib/states/packagegoods-search-keyword.state-item';
import { RestaurantGoodsSearchKeyStateItem } from '../lib/states/restaurant-goods-search-key.state-item';
import { RestaurantMrhstSearchKeyStateItem } from '../lib/states/restaurant-mrhst-search-keyword.state-item';
import { rxStompConfig } from '../rx-stomp.config';
import { routes } from './app.routes';
import { DialogService } from './services/dialog.service';

registerLocaleData(localeKo);
registerLocaleData(localeJa);

registerSwiper();

const pwUpdateOption: PwUpdateOption = {
  messageTranslateKey: 'updateMessage',
  actionTranslateKey: 'updateAction',
  updateCheckInterval: 30000,
};

const rxStompServiceFactory = (platformService: PlatformService): RxStomp => {
  const rxStomp = new RxStompService();
  rxStomp.configure(rxStompConfig);

  // 브라우저 환경에서만 활성화
  if (platformService.isPlatformBrowser) {
    rxStomp.activate();
  }
  return rxStomp;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !!environment.name,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideHttpClient(
      // withFetch(),
      withJsonpSupport(),
      withInterceptors([
        loggingInterceptor,
        cacheInterceptor,
        actingBrandInterceptor,
        authInterceptor,
        errorToastInterceptor,
      ]),
    ),
    // 🔴 CRUD 완료 alert 의 구독자. 주입처가 없으면 생성되지 않아 alert 가 조용히 사라진다
    // (2026-02-14 inject() 전환 때 AppComponent 주입이 빠졌던 사고). 빼지 말 것.
    provideGlobalRequestHandler(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json',
        useHttpBackend: true,
      }),
    }),
    { provide: PW_UPDATE_OPTION, useValue: pwUpdateOption },
    {
      provide: PW_EDITOR_OPTION,
      useValue: <PwEditorOption>{
        defaultFileServerUrl: environment.fileServerUrl,
        defaultOptions: {
          fontNames: [
            'Arial',
            'Arial Black',
            'Comic Sans MS',
            'Courier New',
            'Helvetica Neue',
            'Helvetica',
            'Impact',
            'Lucida Grande',
            'Tahoma',
            'Times New Roman',
            'Verdana',
            'Noto Sans JP',
            'Noto Sans KR',
            'Meiryo',
          ],
        },
      },
    },
    {
      provide: RxStompService,
      useFactory: (platformService: PlatformService): RxStomp => {
        return rxStompServiceFactory(platformService);
      },
      deps: [PlatformService],
    },
    {
      provide: DIALOG_SERVICE,
      useValue: DialogService,
    },
    {
      provide: LOCALE_ID,
      useFactory: (storageService: StorageService): string => {
        return storageService.get('lang') ?? 'ko';
      },
      deps: [StorageService],
    },
    provideClientHydration(),
    provideZoneChangeDetection(),
    provideEchartsCore({
      echarts: () => import('echarts'),
    }),
    BrandPageStateItem,
    PackagegoodsSearchKeyStateItem,
    PackagegoodsAvailMrhstStateItem,
    CodemasterUserClassSearchKeyStateItem,
    CodemasterServiceRegionCodeStateItem,
    CarMrhstSearchKeyStateItem,
    RestaurantMrhstSearchKeyStateItem,
    RestaurantGoodsPageStateItem,
    HotelMrhstSearchKeyStateItem,
    AttractionMrhstSearchKeyStateItem,
    CarGoodsSearchKeyStateItem,
    RestaurantGoodsSearchKeyStateItem,
    AdminSearchKeyStateItem,
    PackagegoodsBookingListStateItem,
    BrandSearchKeyStateItem,
    HomepageBuilderSearchKeyStateItem,
  ],
};
